import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spacer,
    Text,
    VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Alert, ServiceCheckbox } from '@acp/core-ui';
import { useEffect, useMemo } from 'react';
import { UpdatePropertyDocument } from '@acp/shared';
import { DocumentTypeSelect } from './document-type-select';
import { useDocumentServices } from '../hooks/use-document-services';

const formSchema = z.object({
    documentType: z.string({
        required_error: 'Document type is required',
    }),
    services: z.array(z.object({
        code: z.string(),
    })),
    otherServiceName: z.string().max(200, 'Other service name must be less than 200 characters').optional(),
});

type FormValues = z.infer<typeof formSchema>;
type DefaultDocumentValues = {
    type: {
        code: string;
    };
    services: {
        code: string;
        otherServiceName?: string;
    }[];
};

function transformFormValuesToDocumentUpdate(
    formValues: FormValues
): UpdatePropertyDocument {
    return {
        type: {
            code: formValues.documentType,
        },
        services: formValues.services.map((service) => ({
            code: service.code,
            otherServiceName: service.code === 'OTHER' ? formValues.otherServiceName : undefined,
        })),
    };
}

function transformDocumentToFormValues(
    documentValues: DefaultDocumentValues
): FormValues {
    return {
        documentType: documentValues.type.code,
        services: documentValues.services.map((service) => ({
            code: service.code,
        })),
        otherServiceName: documentValues.services.find((service) => service.code === 'OTHER')?.otherServiceName,
    };
}

function UpdateDocumentModal({
    fileName,
    onSubmit,
    isLoading,
    isOpen,
    onClose,
    isCurrentlyUploading,
    defaultValues,
}: {
    fileName: string;
    onSubmit: (data: UpdatePropertyDocument) => void;
    isLoading?: boolean;
    onClose: () => void;
    isOpen: boolean;
    isCurrentlyUploading: boolean;
    defaultValues?: DefaultDocumentValues;
}) {
    const servicesQuery = useDocumentServices();

    const {
        handleSubmit,
        control,
        reset,
        watch,
    } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: defaultValues === undefined ? {
            services: [],
        } : transformDocumentToFormValues(defaultValues),
    });

    useEffect(() => {
        if (defaultValues === undefined) return;
        reset(transformDocumentToFormValues(defaultValues));
    }, [defaultValues]);

    const services = watch('services');

    const displayOtherServiceName = useMemo(
        () => services.filter((service) => service.code === 'OTHER').length > 0,
        [services]
    );

    const handleClose = () => {
        if (defaultValues === undefined) return;
        reset(transformDocumentToFormValues(defaultValues));
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            closeOnEsc={isLoading !== true}
            closeOnOverlayClick={isLoading !== true}
            onClose={isLoading !== true ? handleClose : () => {}}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Update document attributes</ModalHeader>
                <ModalBody>
                    {
                        isCurrentlyUploading && (
                            <Alert title="Upload in progress" status="info" mb={5}>
                                This document is currently being uploaded.
                                Any changes will be saved after upload has completed successfully.
                            </Alert>
                        )
                    }
                    <VStack gap={5} alignItems="start" width="100%">
                        <FormControl>
                            <FormLabel htmlFor="none">
                                File name
                            </FormLabel>
                            <Text>{fileName}</Text>
                        </FormControl>
                        <Controller
                            control={control}
                            name="documentType"
                            render={({ field, fieldState }) => (
                                <FormControl
                                    isInvalid={fieldState.invalid}
                                    isRequired
                                    mb={2}
                                >
                                    <FormLabel htmlFor="none">
                                        Document type
                                    </FormLabel>
                                    <DocumentTypeSelect
                                        value={field.value}
                                        onChange={(documentType) => {
                                            field.onChange(documentType?.value);
                                        }}
                                    />
                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />

                        <Controller
                            control={control}
                            name="services"
                            render={({ field, fieldState }) => (
                                <FormControl
                                    isInvalid={fieldState.invalid}
                                    isRequired
                                    mb={2}
                                >
                                    <FormLabel htmlFor="none">
                                        Services
                                    </FormLabel>
                                    <HStack flexWrap="wrap">
                                        { servicesQuery.data !== undefined && (
                                            servicesQuery.data.map((service) => (
                                                <ServiceCheckbox
                                                    key={service.code}
                                                    serviceIcon={service.icon}
                                                    serviceName={service.name}
                                                    onChange={(isChecked) => {
                                                        const hasExistingService = field.value.find(
                                                            (s) => s.code === service.code
                                                        ) !== undefined;

                                                        if (isChecked && hasExistingService) {
                                                            return;
                                                        }

                                                        if (isChecked) {
                                                            field.onChange([
                                                                ...field.value,
                                                                {
                                                                    code: service.code,
                                                                },
                                                            ]);
                                                        } else {
                                                            field.onChange(
                                                                field.value.filter(
                                                                    (s) => s.code !== service.code
                                                                )
                                                            );
                                                        }
                                                    }}
                                                    isChecked={
                                                        field.value.some(
                                                            (s) => s.code === service.code
                                                        )
                                                    }
                                                />
                                            ))
                                        )}
                                    </HStack>
                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />

                        {displayOtherServiceName && (
                            <Controller
                                control={control}
                                name="otherServiceName"
                                render={({ field, fieldState }) => (
                                    <FormControl
                                        isInvalid={fieldState.invalid}
                                        mb={2}
                                    >
                                        <FormLabel htmlFor="none">
                                            Other service name
                                        </FormLabel>
                                        <Input
                                            {...field}
                                            type="text"
                                        />
                                        {fieldState.error && (
                                            <FormErrorMessage>
                                                {fieldState.error.message}
                                            </FormErrorMessage>
                                        )}
                                    </FormControl>
                                )}
                            />
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="ghost"
                        colorScheme="muted"
                        size="md"
                        onClick={handleClose}
                        isDisabled={isLoading}
                    >
                        Close
                    </Button>
                    <Spacer />
                    <Button
                        variant="solid"
                        colorScheme="secondary"
                        size="md"
                        onClick={handleSubmit(
                            (data) => onSubmit(transformFormValuesToDocumentUpdate(data))
                        )}
                        isLoading={isLoading}
                    >
                        Update
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export { UpdateDocumentModal };
